<script setup>
import { ref, reactive, computed, onMounted } from 'vue';
import { useStore } from 'dashboard/composables/store';
import { useI18n } from 'vue-i18n';
import { useVuelidate } from '@vuelidate/core';
import { required, minLength } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';

import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  mode: {
    type: String,
    default: 'add',
    validator: value => ['add', 'edit'].includes(value),
  },
  selectedSubject: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['close']);

const store = useStore();
const { t } = useI18n();

const name = ref('');
const nameInput = ref(null);

const submitState = reactive({
  showLoading: false,
});

const rules = computed(() => ({
  name: { required, minLength: minLength(2) },
}));

const v$ = useVuelidate(rules, { name });

const resetForm = () => {
  name.value = '';
  v$.value.$reset();
};

const populateEditForm = () => {
  name.value = props.selectedSubject?.name || '';
};

onMounted(() => {
  if (props.mode === 'edit') {
    populateEditForm();
  }
  nameInput.value?.focus();
});

const getTranslationKey = base => {
  return props.mode === 'edit'
    ? `CONVERSATION_RESOLUTION_SUBJECT.EDIT.${base}`
    : `CONVERSATION_RESOLUTION_SUBJECT.ADD.${base}`;
};

const modalTitle = computed(() => t(getTranslationKey('TITLE')));
const modalDescription = computed(() => t(getTranslationKey('DESC')));
const submitButtonText = computed(() => t(getTranslationKey('SUBMIT')));

const handleSubmit = async () => {
  v$.value.$touch();
  if (v$.value.$invalid) return;

  submitState.showLoading = true;
  try {
    const payload = { name: name.value };

    if (props.mode === 'edit') {
      await store.dispatch(
        'conversationResolutionSubject/updateConversationResolutionSubject',
        {
          id: props.selectedSubject.id,
          ...payload,
        }
      );
      useAlert(t('CONVERSATION_RESOLUTION_SUBJECT.EDIT.API.SUCCESS_MESSAGE'));
    } else {
      await store.dispatch(
        'conversationResolutionSubject/createConversationResolutionSubject',
        payload
      );
      useAlert(t('CONVERSATION_RESOLUTION_SUBJECT.ADD.API.SUCCESS_MESSAGE'));
    }

    resetForm();
    emit('close');
  } catch (error) {
    // The backend raises the AR uniqueness error ("Name has already been
    // taken") when the name clashes with an existing active subject. Surface a
    // friendly, translated message for that case, and a generic translated
    // message for every other failure.
    const isNameTaken = error?.message?.includes('has already been taken');
    const errorMessage = isNameTaken
      ? t('CONVERSATION_RESOLUTION_SUBJECT.FORM.API.NAME_TAKEN')
      : t('CONVERSATION_RESOLUTION_SUBJECT.FORM.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    submitState.showLoading = false;
  }
};

const isSubmitDisabled = computed(
  () => v$.value.$invalid || submitState.showLoading
);
</script>

<template>
  <div class="flex flex-col h-auto overflow-auto">
    <woot-modal-header
      :header-title="modalTitle"
      :header-content="modalDescription"
    />
    <form class="flex flex-col w-full" @submit.prevent="handleSubmit">
      <div class="w-full">
        <label :class="{ 'text-red-500': v$.name.$error }">
          {{ $t('CONVERSATION_RESOLUTION_SUBJECT.FORM.NAME.LABEL') }}
          <input
            ref="nameInput"
            v-model.trim="name"
            type="text"
            :class="{ '!border-red-500': v$.name.$error }"
            :placeholder="
              $t('CONVERSATION_RESOLUTION_SUBJECT.FORM.NAME.PLACEHOLDER')
            "
            @blur="v$.name.$touch"
          />
          <span v-if="v$.name.$error" class="text-red-500 text-xs">
            {{ $t('CONVERSATION_RESOLUTION_SUBJECT.FORM.NAME.ERROR') }}
          </span>
        </label>
      </div>

      <div class="flex flex-row justify-end w-full gap-2 px-0 py-2">
        <NextButton
          type="submit"
          variant="solid"
          color="blue"
          :label="submitButtonText"
          :disabled="isSubmitDisabled"
          :is-loading="submitState.showLoading"
        />
        <button class="button clear" @click.prevent="emit('close')">
          {{ $t('CONVERSATION_RESOLUTION_SUBJECT.FORM.CANCEL_BUTTON_TEXT') }}
        </button>
      </div>
    </form>
  </div>
</template>
